interface HtmlNode {
    type?: string;
    tagName?: string;
    value?: string;
    properties?: Record<string, unknown>;
    children?: HtmlNode[];
}

interface MarkdownPoint {
    line?: number;
    offset?: number;
}

interface MarkdownPosition {
    start?: MarkdownPoint;
    end?: MarkdownPoint;
}

interface MarkdownNodeData {
    hName?: string;
    hProperties?: Record<string, unknown>;
    hChildren?: HtmlNode[];
}

interface MarkdownNode {
    type: string;
    position?: MarkdownPosition;
    data?: MarkdownNodeData;
    children?: MarkdownNode[];
}

const SAFE_MARKDOWN_LINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);
const VISIBLE_BLANK_LINE_CONTAINER_TYPES = new Set(['root', 'blockquote', 'listItem']);
const MARKDOWN_STATUS_PREFIX = 'HONEYCOMB_MARKDOWN_STATUS:';
const MARKDOWN_STATUS_CLASSES = [
    'inline-flex',
    'items-center',
    'gap-1',
    'whitespace-nowrap',
    'rounded-md',
    'border',
    'border-warning',
    'bg-yellow-50',
    'px-1.5',
    'py-0.5',
    'align-baseline',
    'text-sm',
    'font-semibold',
    'leading-tight',
    'text-warning-strong',
    'not-italic',
];

export function createMarkdownStatus(label: string): string {
    return `\`${MARKDOWN_STATUS_PREFIX}${encodeURIComponent(label)}\``;
}

function sanitizeMarkdownLinkHref(href: string): string | null {
    const trimmedHref = href.trim();

    if (!trimmedHref) {
        return null;
    }

    if (/^(#|\/(?!\/)|\.\.?\/|\?)/.test(trimmedHref)) {
        return trimmedHref;
    }

    try {
        const url = new URL(trimmedHref, 'https://example.invalid');
        return SAFE_MARKDOWN_LINK_PROTOCOLS.has(url.protocol) ? trimmedHref : null;
    } catch {
        return null;
    }
}

function isExternalMarkdownLink(href: string): boolean {
    try {
        const url = new URL(href);
        return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
        return false;
    }
}

function visitHtmlTree(node: HtmlNode, visitor: (currentNode: HtmlNode) => void): void {
    visitor(node);

    node.children?.forEach((childNode) => {
        visitHtmlTree(childNode, visitor);
    });
}

export function rehypeSafeMarkdownLinks() {
    return (tree: HtmlNode) => {
        visitHtmlTree(tree, (node) => {
            if (node.type !== 'element' || node.tagName !== 'a') {
                return;
            }

            const href = typeof node.properties?.href === 'string' ? node.properties.href : null;
            if (!href) {
                return;
            }

            const safeHref = sanitizeMarkdownLinkHref(href);
            if (!safeHref) {
                delete node.properties?.href;
                delete node.properties?.target;
                delete node.properties?.rel;
                return;
            }

            node.properties = {
                ...node.properties,
                href: safeHref,
            };

            if (isExternalMarkdownLink(safeHref)) {
                node.properties.target = '_blank';
                node.properties.rel = 'noopener noreferrer';
            }
        });
    };
}

export function rehypeMarkdownStatuses() {
    return (tree: HtmlNode) => {
        visitHtmlTree(tree, (node) => {
            if (node.type !== 'element' || node.tagName !== 'code') {
                return;
            }

            const encodedStatus = node.children?.[0]?.value;
            if (
                node.children?.length !== 1 ||
                typeof encodedStatus !== 'string' ||
                !encodedStatus.startsWith(MARKDOWN_STATUS_PREFIX)
            ) {
                return;
            }

            let label: string;
            try {
                label = decodeURIComponent(encodedStatus.slice(MARKDOWN_STATUS_PREFIX.length));
            } catch {
                return;
            }

            node.tagName = 'span';
            node.properties = {
                className: MARKDOWN_STATUS_CLASSES,
                'data-markdown-status': 'warning',
            };
            node.children = [
                {
                    type: 'element',
                    tagName: 'span',
                    properties: { 'aria-hidden': 'true' },
                    children: [{ type: 'text', value: '⚠' }],
                },
                { type: 'text', value: label },
            ];
        });
    };
}

function createVisibleBlankLineNode(blankLineCount: number): MarkdownNode {
    return {
        type: 'paragraph',
        children: [],
        data: {
            hName: 'span',
            hProperties: {
                className: ['markdown-visible-blank-lines'],
                'data-preserved-blank-lines': blankLineCount,
                'aria-hidden': 'true',
            },
            hChildren: Array.from(
                { length: blankLineCount },
                (): HtmlNode => ({
                    type: 'element',
                    tagName: 'br',
                    properties: {},
                    children: [],
                })
            ),
        },
    };
}

function getBlankLineCount(previousNode: MarkdownNode, nextNode: MarkdownNode): number {
    const previousEndLine = previousNode.position?.end?.line;
    const nextStartLine = nextNode.position?.start?.line;

    if (typeof previousEndLine !== 'number' || typeof nextStartLine !== 'number') {
        return 0;
    }

    return Math.max(0, nextStartLine - previousEndLine - 1);
}

function getLeadingBlankLineCount(node: MarkdownNode, firstChildNode: MarkdownNode): number {
    const nodeStartLine = node.position?.start?.line;
    const firstChildStartLine = firstChildNode.position?.start?.line;

    if (typeof nodeStartLine !== 'number' || typeof firstChildStartLine !== 'number') {
        return 0;
    }

    return Math.max(0, firstChildStartLine - nodeStartLine);
}

function countLineBreaks(sourceText: string): number {
    return sourceText.match(/\r\n|\r|\n/g)?.length ?? 0;
}

function getTrailingBlankLineCount(
    node: MarkdownNode,
    lastChildNode: MarkdownNode,
    sourceText: string
): number {
    const nodeEndOffset = node.position?.end?.offset;
    const lastChildEndOffset = lastChildNode.position?.end?.offset;

    if (typeof nodeEndOffset !== 'number' || typeof lastChildEndOffset !== 'number') {
        return 0;
    }

    const trailingSourceText = sourceText.slice(lastChildEndOffset, nodeEndOffset);
    return Math.max(0, countLineBreaks(trailingSourceText) - 1);
}

function preserveVisibleBlankLines(node: MarkdownNode, sourceText: string): MarkdownNode {
    const transformedChildren = node.children?.map((childNode) =>
        preserveVisibleBlankLines(childNode, sourceText)
    );
    if (!transformedChildren) {
        return node;
    }

    if (!VISIBLE_BLANK_LINE_CONTAINER_TYPES.has(node.type)) {
        return {
            ...node,
            children: transformedChildren,
        };
    }

    const childrenWithVisibleBlankLines: MarkdownNode[] = [];
    const firstChildNode = transformedChildren[0];
    if (firstChildNode) {
        const leadingBlankLineCount = getLeadingBlankLineCount(node, firstChildNode);
        if (leadingBlankLineCount > 0) {
            childrenWithVisibleBlankLines.push(createVisibleBlankLineNode(leadingBlankLineCount));
        }
    }

    transformedChildren.forEach((childNode, childIndex) => {
        childrenWithVisibleBlankLines.push(childNode);

        const nextChildNode = transformedChildren[childIndex + 1];
        if (!nextChildNode) {
            return;
        }

        const blankLineCount = getBlankLineCount(childNode, nextChildNode);
        if (blankLineCount > 0) {
            childrenWithVisibleBlankLines.push(createVisibleBlankLineNode(blankLineCount));
        }
    });

    const lastChildNode = transformedChildren[transformedChildren.length - 1];
    if (lastChildNode) {
        const trailingBlankLineCount = getTrailingBlankLineCount(node, lastChildNode, sourceText);
        if (trailingBlankLineCount > 0) {
            childrenWithVisibleBlankLines.push(createVisibleBlankLineNode(trailingBlankLineCount));
        }
    } else if (node.type === 'root') {
        const rootStartLine = node.position?.start?.line;
        const rootEndLine = node.position?.end?.line;
        if (typeof rootStartLine === 'number' && typeof rootEndLine === 'number') {
            const blankLineCount = Math.max(0, rootEndLine - rootStartLine);
            if (blankLineCount > 0) {
                childrenWithVisibleBlankLines.push(createVisibleBlankLineNode(blankLineCount));
            }
        }
    }

    return {
        ...node,
        children: childrenWithVisibleBlankLines,
    };
}

export function remarkPreserveVisibleBlankLines() {
    return (tree: MarkdownNode, file: { value?: string }) =>
        preserveVisibleBlankLines(tree, typeof file.value === 'string' ? file.value : '');
}
